import { useMemo } from 'react';
import { TableProps } from 'antd';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import CustomTable from '../../../../shared/components/CustomTable';
import { buildColumns } from './columns';
import { DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER } from '../../../../hooks/useUsersList';
import { USER_EMPTY_COPY } from '../../constants';
import type { UserRecord, UserSortField, UserSortOrder } from '../../../../types/UserManagementTypes';
import type { UserAction } from '../../constants';
import styles from './UsersTable.module.scss';

// Below this the body scrolls horizontally; above it the table stretches. It
// has to be a number: rc-table writes scroll.x onto the table as an inline
// width, which no stylesheet min-width could override.
const TABLE_MIN_WIDTH_PX = 1040;

interface UsersTableProps {
  users: UserRecord[];
  isLoadingUsers: boolean;
  isUsersLoadingError: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  sortBy: UserSortField;
  sortOrder: UserSortOrder;
  onSortChange: (field: UserSortField, order: UserSortOrder) => void;
  onAction: (action: UserAction, user: UserRecord, anchor: HTMLElement | null) => void;
}

const UsersTable = ({
  users,
  isLoadingUsers,
  isUsersLoadingError,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  sortBy,
  sortOrder,
  onSortChange,
  onAction,
}: UsersTableProps) => {
  const columns = useMemo(() => buildColumns(onAction, sortBy, sortOrder), [onAction, sortBy, sortOrder]);

  const handleReachEnd = () => {
    if (isFetchingNextPage || !hasNextPage) return;
    fetchNextPage();
  };

  const handleTableChange: TableProps<UserRecord>['onChange'] = (_pagination, _filters, sorter) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (activeSorter?.order && typeof activeSorter.columnKey === 'string') {
      onSortChange(activeSorter.columnKey as UserSortField, activeSorter.order as UserSortOrder);
    } else {
      onSortChange(DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER);
    }
  };

  return (
    <CustomTable<UserRecord>
      className={styles.usersTable}
      fillHeight
      rowKey="id"
      dataSource={users}
      columns={columns}
      loading={isLoadingUsers}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: TABLE_MIN_WIDTH_PX }}
      // Held back during the first fetch so the spinner isn't captioned "no users".
      locale={{
        emptyText: (
          <ConditionalRender visible={!isLoadingUsers} hideFallback>
            <ConditionalRender visible={isUsersLoadingError} fallback={<p className={styles.emptyState}>{USER_EMPTY_COPY.NO_USERS}</p>}>
              <p className={styles.errorState}>{USER_EMPTY_COPY.LOAD_ERROR}</p>
            </ConditionalRender>
          </ConditionalRender>
        ),
      }}
      onReachEnd={handleReachEnd}
      onChange={handleTableChange}
      showSorterTooltip={false}
    />
  );
};

export default UsersTable;
